import { isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  OnDestroy,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import Matter from 'matter-js';
// @ts-ignore - poly-decomp doesn't have types
import decomp from 'poly-decomp';

// Set poly-decomp for complex shapes
Matter.Common.setDecomp(decomp);

@Component({
  selector: 'app-snow-overlay-physics',
  imports: [],
  templateUrl: './snow-overlay-physics.component.html',
  styleUrl: './snow-overlay-physics.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SnowOverlayPhysicsComponent implements OnDestroy {
  canvasRef = viewChild<ElementRef<HTMLCanvasElement>>('canvas');

  debug = input<boolean>(false);
  showDebug = signal(false);
  showStats = signal(false); // Performance metrics

  // --- Tunables (matching snow-overlay.component.ts style) ---
  private readonly FLAKE_COUNT = 1400;
  private readonly SPAWN_RATE = 50; // ms between spawns (50)
  private readonly WIND_FORCE_INTENSITY_BASE = 0.000003; // Base wind force (scaled by screen width)
  private readonly MAX_JUMP_HEIGHT = 0.05; // X% of screen height (more subtle)
  private readonly SCROLL_SLOW_MO_SCALE = 0.75; // Time scale during scroll (0.3 = 30% speed)
  private readonly FADE_START_HEIGHT_RATIO = 0.3; // Start fading halfway to target height
  private readonly FADE_DURATION = 200; // 200ms fade duration
  private readonly MAX_SETTLED_SNOW_AGE = 3000; // Remove settled snow after x seconds
  private readonly SETTLED_SNOW_CLEANUP_INTERVAL = 5000; // Check every 5 seconds
  private readonly MAX_SNOW_REMOVALS_PER_CYCLE = 5; // Remove max 5 snowflakes per cleanup cycle
  private readonly PLOW_ACCELERATION = 0.5; // Velocity increase per frame when key held
  private readonly PLOW_MAX_VELOCITY = 8; // Maximum horizontal velocity

  // Dynamic wind force (calculated based on screen width)
  private windForceIntensity = this.WIND_FORCE_INTENSITY_BASE;
  private engine: Matter.Engine | null = null;
  private render: Matter.Render | null = null;
  private runner: Matter.Runner | null = null;
  private snowflakes: Matter.Body[] = [];
  private plow: Matter.Body | null = null; // Arrow-key controlled plow object
  private snowInterval: number | null = null;
  private windForceInterval: number | null = null;
  private cleanupInterval: number | null = null;
  private scrollTimeout: number | null = null; // Track scroll timeout to prevent memory leak
  private time = 0;

  // Keyboard controls
  private keysPressed = new Set<string>();
  private handleKeyDownBound: ((event: KeyboardEvent) => void) | null = null;
  private handleKeyUpBound: ((event: KeyboardEvent) => void) | null = null;

  showTutorial = signal(true);

  // Cached dimensions (updated on init and resize)
  private width = 0;
  private height = 0;
  private horizontalExtension = 0;
  private verticalExtension = 0;
  private floorHeight = 0;

  // Scroll tracking
  private lastScrollY = 0;
  private scrollVelocity = 0;
  private timeScaleTarget = 1;
  private fadingSnowflakes = new Map<Matter.Body, number>(); // body -> fade start time
  private snowflakeBirthTimes = new Map<Matter.Body, number>(); // body -> birth timestamp
  private handleScrollBound: (() => void) | null = null;
  private handleResizeBound: (() => void) | null = null;

  private platformId = inject(PLATFORM_ID);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      afterNextRender(() => {
        this.initPhysics();
      });
    }
  }

  private initPhysics(): void {
    const canvas = this.canvasRef()?.nativeElement;
    if (!canvas) return;

    // Cache dimensions for reuse
    this.updateDimensions();

    // Create engine with reduced gravity for gentle fall
    this.engine = Matter.Engine.create({
      gravity: {
        x: 0,
        y: 0.15, // Much gentler gravity than default (1)
        scale: 0.001,
      },
      //enableSleeping: true,
    });
    const world = this.engine.world;

    // Create renderer
    this.render = Matter.Render.create({
      canvas: canvas,
      engine: this.engine,
      options: {
        width: this.width,
        height: this.height,
        wireframes: this.showDebug(),
        background: 'transparent',
        pixelRatio: window.devicePixelRatio,
        showStats: this.showStats(), // Show performance stats
        showPerformance: this.showStats(), // Show FPS and other metrics
      },
    });

    Matter.Render.run(this.render);

    // Create runner
    this.runner = Matter.Runner.create();
    Matter.Runner.run(this.runner, this.engine);

    // Add boundaries extending 5% beyond visible screen
    const wallThickness = 1;
    const extendedWidth = this.width + this.horizontalExtension * 2;
    const wallHeight = this.height + this.verticalExtension; // Only extend top for walls

    const boundaries = [
      // Floor at bottom of visible area (no extension)
      Matter.Bodies.rectangle(this.width / 2, this.floorHeight, extendedWidth, wallThickness, {
        isStatic: true,
        label: 'floor',
        render: {
          fillStyle: this.showDebug() ? '#666' : 'transparent',
        },
      }),
      // Left wall beyond left edge (extends only at top)
      Matter.Bodies.rectangle(-this.horizontalExtension, (this.height - this.verticalExtension) / 2, wallThickness, wallHeight, {
        isStatic: true,
        label: 'leftWall',
        render: {
          fillStyle: this.showDebug() ? '#666' : 'transparent',
        },
      }),
      // Right wall beyond right edge (extends only at top)
      Matter.Bodies.rectangle(
        this.width + this.horizontalExtension,
        (this.height - this.verticalExtension) / 2,
        wallThickness,
        wallHeight,
        {
          isStatic: true,
          label: 'rightWall',
          render: {
            fillStyle: this.showDebug() ? '#666' : 'transparent',
          },
        }
      ),
    ];

    Matter.Composite.add(world, boundaries);

    // Add plow (arrow-key controlled tractor) only on non-mobile devices
    if (!this.isMobileDevice()) {
      this.createPlow();
      // Setup keyboard controls for plow
      this.setupKeyboardControls();
    }

    // Start spawning snowflakes
    this.startSnowfall();

    // Apply wind force periodically for fluttering effect
    this.applyWindForce();

    // Start periodic cleanup of old settled snow
    this.startSettledSnowCleanup();

    // Setup scroll interaction
    this.setupScrollInteraction();

    // Setup engine events for time scaling and fade effects
    this.setupEngineEvents();

    // Handle window resize
    this.handleResizeBound = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResizeBound, { passive: true });
  }

  isMobileDevice(): boolean {
    // Check for mobile/tablet devices using multiple methods
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i.test(userAgent);
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isSmallScreen = window.innerWidth <= 768; // Typical mobile breakpoint

    return isMobileUA || (isTouchDevice && isSmallScreen);
    //return false;
  }

  private updateDimensions(): void {
    // Cache dimension calculations
    this.width = window.innerWidth;
    this.height = window.innerHeight;
    this.horizontalExtension = this.width * 0.05; // 5% extension
    this.verticalExtension = this.height * 0.05; // 5% extension for top only
    this.floorHeight = this.height; // Floor at bottom of visible area

    // Update wind force based on screen width
    this.updateWindForce();
  }

  private updateWindForce(): void {
    // Scale wind force based on screen width
    // Mobile (< 768px): 40% strength
    // Tablet (768-1024px): 60% strength
    // Desktop (> 1024px): 100% strength
    if (this.width <= 768) {
      this.windForceIntensity = this.WIND_FORCE_INTENSITY_BASE * 0.4; // 40% for mobile
    } else if (this.width <= 1024) {
      this.windForceIntensity = this.WIND_FORCE_INTENSITY_BASE * 0.6; // 60% for tablet
    } else {
      this.windForceIntensity = this.WIND_FORCE_INTENSITY_BASE; // 100% for desktop
    }
  }

  private createPlow(): void {
    if (!this.engine) return;

    // Guard: only create plow once
    if (this.plow) {
      return;
    }

    // Preload the truck image before creating the sprite body
    const truckImage = new Image();
    const truckTexturePath = './assets/christmas/tractor-5.png';

    truckImage.onload = () => {
      this.createPlowWithLoadedImage(truckTexturePath);
    };

    truckImage.onerror = () => {
      console.error('Failed to load truck image:', truckTexturePath);
      // Create plow without texture as fallback
      this.createPlowWithLoadedImage('');
    };

    truckImage.src = truckTexturePath;
  }

  private createPlowWithLoadedImage(texturePath: string): void {
    if (!this.engine) return;

    // Guard: only create plow once
    if (this.plow) {
      return;
    }

    // Hardcoded vertices from SVG polygon points
    // Original: "618.6 360.1 583.3 316.8 577 264.1 615.9 218.4 638.7 220.5 639.4 206.6 338.6 148.4 340 36.8 114 33.3 108.5 151.2 50.9 187.9 50.9 360.2 618.6 360.1"
    const originalVertices: Matter.Vector[] = [
      { x: 618.6, y: 360.1 },
      { x: 583.3, y: 316.8 },
      { x: 577, y: 264.1 },
      { x: 615.9, y: 218.4 },
      { x: 638.7, y: 220.5 },
      { x: 639.4, y: 206.6 },
      { x: 338.6, y: 148.4 },
      { x: 340, y: 36.8 },
      { x: 114, y: 33.3 },
      { x: 108.5, y: 151.2 },
      { x: 50.9, y: 187.9 },
      { x: 50.9, y: 360.2 },
    ];

    try {
      // Scale down the tractor (original is 668x360.5, scale to ~40% size)
      const scale = 0.4;
      const scaledVertices = Matter.Vertices.scale(originalVertices, scale, scale, Matter.Vector.create(0, 0));
      const tractorSize = Matter.Bounds.create(scaledVertices);

      const weirdOffsetFix = 25;

      // Position at very left of screen, on the ground
      const width = tractorSize.max.x - tractorSize.min.x;
      const height = tractorSize.max.y - tractorSize.min.y;

      // Simple positioning: left side + half width for centering
      const plowX = width / 2 - 30; // Half width + small margin from left edge
      const plowY = this.floorHeight - height / 2 + weirdOffsetFix + 2; // Position so bottom touches floor

      // Create collision body from hardcoded vertices (simple convex polygon)
      const collisionBody = Matter.Bodies.fromVertices(
        plowX,
        plowY,
        [scaledVertices], // Wrap in array
        {
          isStatic: false,
          label: 'plow-collision',
          restitution: 0.01, // Almost no bounce (reduced from 0.2)
          friction: 1.5, // Higher friction to dampen collisions (increased from 0.99)
          frictionStatic: 2, // Very high static friction (increased from 1)
          frictionAir: 1,
          density: 10,
          inertia: Infinity,
          collisionFilter: {
            category: 0x0002,
            mask: 0xffff,
          },
          render: {
            visible: false,
          },
        },
        true // Decomposition
      );

      if (!collisionBody) {
        console.error('Failed to create collision body');
        return;
      }

      // Extract the decomposed collision pieces (skip index 0 which is the parent)
      const collisionPieces = collisionBody.parts.slice(1);

      // Create sprite body config
      const spriteBodyConfig: any = {
        isStatic: false,
        label: 'plow-sprite',
        isSensor: true, // Sensor bodies don't cause physical collisions
        collisionFilter: {
          category: 0x0000, // No collision category
          mask: 0x0000, // Doesn't collide with anything
        },
        render: {
          // Only add sprite if texture was loaded successfully
          ...(texturePath
            ? {
                sprite: {
                  texture: texturePath,
                  xScale: width / 668, // Original SVG width
                  yScale: height / 360, // Original SVG height (updated viewBox)
                },
              }
            : {
                //fillStyle: '#FF6B35', // Fallback color if image fails
                //strokeStyle: '#000000',
                //lineWidth: 2,
              }),
        },
      };

      // Create a rectangle body for the sprite texture (no collision, just visual)
      const spriteBody = Matter.Bodies.rectangle(
        plowX + weirdOffsetFix / 2,
        plowY - weirdOffsetFix,
        width - weirdOffsetFix,
        height - weirdOffsetFix,
        spriteBodyConfig
      );

      // Create a compound body that combines collision pieces and sprite
      // The compound body will recalculate its center from all parts
      this.plow = Matter.Body.create({
        parts: [...collisionPieces, spriteBody],
        isStatic: false,
        label: 'plow',
        collisionFilter: {
          category: 0x0002, // Set on parent body for mouse interaction
          mask: 0xffff,
        },
      });

      // Store the fixed Y position for later use
      (this.plow as any).fixedY = plowY;

      Matter.Composite.add(this.engine.world, this.plow);
    } catch (vertexError) {
      console.error('Error creating vertices from SVG path:', vertexError);
    }
  }

  private setupKeyboardControls(): void {
    // Handle keydown events
    this.handleKeyDownBound = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault(); // Prevent page scrolling
        this.keysPressed.add(event.key);
        if (this.showTutorial()) {
          this.showTutorial.set(false);
        }
      }
    };

    // Handle keyup events
    this.handleKeyUpBound = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        this.keysPressed.delete(event.key);
      }
    };

    window.addEventListener('keydown', this.handleKeyDownBound);
    window.addEventListener('keyup', this.handleKeyUpBound);
  }

  private startSnowfall(): void {
    // Spawn snowflakes periodically
    this.snowInterval = window.setInterval(() => {
      if (this.snowflakes.length < this.FLAKE_COUNT) {
        this.createSnowflake();
      }
    }, this.SPAWN_RATE);
  }

  private applyWindForce(): void {
    // Apply smooth wind force to create fluttering effect
    this.windForceInterval = window.setInterval(() => {
      if (!this.engine || this.snowflakes.length === 0) return; // Early exit if no snowflakes

      this.time += 0.016; // ~60fps increment

      // Use cached dimensions
      const settledThreshold = this.height * 0.8;

      // Smooth wind that varies over time (like in snow-overlay.component.ts)
      const windBase = Math.sin(this.time * 0.45) + Math.sin(this.time * 0.95) * 0.5;
      const wind = windBase * this.windForceIntensity; // Use dynamic wind force based on screen width

      // Cache wind force object to avoid creating new objects in loop
      const windForce = { x: wind, y: 0 };

      // Only iterate through snowflakes that need wind
      for (let i = 0, len = this.snowflakes.length; i < len; i++) {
        const snowflake = this.snowflakes[i];
        // Only apply wind to falling snow, not settled
        if (snowflake.position.y < settledThreshold) {
          Matter.Body.applyForce(snowflake, snowflake.position, windForce);
        }
      }
    }, 16); // ~60fps
  }

  private startSettledSnowCleanup(): void {
    // Periodically check and remove old settled snow
    this.cleanupInterval = window.setInterval(() => {
      if (!this.engine) return;

      const now = Date.now();
      const settledThreshold = this.height - 100; // Consider settled if near bottom (use cached height)

      // Find old settled snowflakes that are candidates for removal
      const candidates: { snowflake: Matter.Body; age: number; y: number }[] = [];

      this.snowflakes.forEach(snowflake => {
        const birthTime = this.snowflakeBirthTimes.get(snowflake);
        const age = birthTime ? now - birthTime : 0;

        // If snowflake is old, settled, and not already fading
        if (
          age > this.MAX_SETTLED_SNOW_AGE &&
          snowflake.position.y > settledThreshold &&
          Math.abs(snowflake.velocity.y) < 0.1 && // Almost stopped moving
          !this.fadingSnowflakes.has(snowflake)
        ) {
          candidates.push({
            snowflake,
            age,
            y: snowflake.position.y,
          });
        }
      });

      // Early exit if no candidates
      if (candidates.length === 0) return;

      // Only need to find top MAX_SNOW_REMOVALS_PER_CYCLE items, not sort entire array
      const removeCount = Math.min(this.MAX_SNOW_REMOVALS_PER_CYCLE, candidates.length);

      // Partial sort: use selection algorithm to find top N without sorting all
      // This is O(n*k) instead of O(n log n) where k is typically 5
      const toRemove: typeof candidates = [];
      for (let i = 0; i < removeCount; i++) {
        let bestIndex = i;
        let bestScore = this.calculateRemovalPriority(candidates[i]);

        // Find the best candidate from remaining items
        for (let j = i + 1; j < candidates.length; j++) {
          const score = this.calculateRemovalPriority(candidates[j]);
          if (score > bestScore) {
            bestScore = score;
            bestIndex = j;
          }
        }

        // Swap best to current position
        if (bestIndex !== i) {
          const temp = candidates[i];
          candidates[i] = candidates[bestIndex];
          candidates[bestIndex] = temp;
        }

        toRemove.push(candidates[i]);
      }

      // Start fading out selected old settled snow gradually
      toRemove.forEach((item, index) => {
        // Stagger the fade start slightly for more natural effect
        setTimeout(() => {
          if (this.fadingSnowflakes.has(item.snowflake)) return;
          this.fadingSnowflakes.set(item.snowflake, Date.now());
        }, index * 100); // 100ms stagger between each removal
      });
    }, this.SETTLED_SNOW_CLEANUP_INTERVAL);
  }

  // Helper to calculate removal priority (higher = remove first)
  private calculateRemovalPriority(candidate: { y: number; age: number }): number {
    // Prioritize by Y position (lower on screen = higher priority)
    // Then by age as tiebreaker
    return candidate.y * 1000 + candidate.age;
  }

  private setupScrollInteraction(): void {
    this.handleScrollBound = () => {
      const currentScrollY = window.scrollY;
      const deltaY = currentScrollY - this.lastScrollY;

      // Calculate scroll velocity (normalized)
      this.scrollVelocity = Math.abs(deltaY) / 10;
      this.lastScrollY = currentScrollY;

      // Trigger slow-mo effect and snow jump ONLY when scrolling down
      if (deltaY > 1) {
        // deltaY > 0 means scrolling down
        this.timeScaleTarget = this.SCROLL_SLOW_MO_SCALE;
        this.makeSnowJump(this.scrollVelocity);
      }

      // Reset time scale after scroll stops
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }
      this.scrollTimeout = window.setTimeout(() => {
        this.timeScaleTarget = 1;
      }, 300);
    };

    window.addEventListener('scroll', this.handleScrollBound, { passive: true });
  }

  private setupEngineEvents(): void {
    if (!this.engine) return;

    // Smooth time scale transitions (like in timescale.js example)
    Matter.Events.on(this.engine, 'afterUpdate', (event: any) => {
      if (!this.engine) return;

      const timeScale = (event.delta || 1000 / 60) / 1000;

      // Tween the timescale for smooth slow-mo effect
      this.engine.timing.timeScale += (this.timeScaleTarget - this.engine.timing.timeScale) * 12 * timeScale;

      // Lock plow Y position (keep it on ground) and apply keyboard controls
      if (this.plow) {
        const fixedY = (this.plow as any).fixedY;
        if (fixedY !== undefined) {
          Matter.Body.setPosition(this.plow, {
            x: this.plow.position.x,
            y: fixedY,
          });

          // Get current velocity
          let newVelocityX = this.plow.velocity.x;

          // Apply acceleration based on arrow key presses
          if (this.keysPressed.has('ArrowLeft')) {
            newVelocityX -= this.PLOW_ACCELERATION;
          }
          if (this.keysPressed.has('ArrowRight')) {
            newVelocityX += this.PLOW_ACCELERATION;
          }

          // Apply damping when no keys are pressed
          if (!this.keysPressed.has('ArrowLeft') && !this.keysPressed.has('ArrowRight')) {
            newVelocityX *= 0.85; // Decelerate when not pressing keys
          }

          // Clamp velocity to max speed
          newVelocityX = Math.max(-this.PLOW_MAX_VELOCITY, Math.min(this.PLOW_MAX_VELOCITY, newVelocityX));

          // Set the new velocity (always zero out Y velocity)
          Matter.Body.setVelocity(this.plow, {
            x: newVelocityX,
            y: 0,
          });
        }
      }

      // Limit snowflake velocities to prevent fast flying snow
      const maxSnowSpeed = 5; // Maximum speed for snowflakes
      this.snowflakes.forEach(snowflake => {
        const speed = Math.sqrt(snowflake.velocity.x ** 2 + snowflake.velocity.y ** 2);
        if (speed > maxSnowSpeed) {
          // Scale velocity down to max speed
          const scale = maxSnowSpeed / speed;
          Matter.Body.setVelocity(snowflake, {
            x: snowflake.velocity.x * scale,
            y: snowflake.velocity.y * scale,
          });
        }
      });

      // Update fading snowflakes
      this.updateFadingSnowflakes();
    });
  }

  private makeSnowJump(velocity: number): void {
    if (!this.engine) return;

    // Use cached dimensions
    const floorY = this.floorHeight;
    const maxJumpForce = this.height * this.MAX_JUMP_HEIGHT * 0.00004; // Reduced force multiplier for gentler jump

    // Get settled snowflakes (those near the floor and not already fading)
    const settledSnowflakes = this.snowflakes.filter(snowflake => {
      return (
        snowflake.position.y > floorY - 100 &&
        Math.abs(snowflake.velocity.y) < 0.5 && // Only truly settled snow
        !this.fadingSnowflakes.has(snowflake)
      );
    });

    const settledCount = settledSnowflakes.length;
    if (settledCount === 0) return; // Early exit if no settled snow

    // Calculate jump count (cache length)
    const jumpCount = Math.min(Math.floor(settledCount * (velocity / 30)), settledCount);
    if (jumpCount === 0) return; // Early exit if nothing to jump

    // Use Fisher-Yates shuffle for first N elements (efficient random selection without duplicates)
    for (let i = 0; i < jumpCount; i++) {
      // Pick random index from remaining unselected elements
      const randomIndex = i + Math.floor(Math.random() * (settledCount - i));

      // Swap elements (Fisher-Yates)
      const temp = settledSnowflakes[i];
      settledSnowflakes[i] = settledSnowflakes[randomIndex];
      settledSnowflakes[randomIndex] = temp;

      const snowflake = settledSnowflakes[i];

      // No need to check has() again - we filtered already
      // Apply gentle upward force based on scroll velocity
      const jumpForce = Math.min(velocity * maxJumpForce * 0.5, maxJumpForce); // More gentle
      const randomness = 0.6 + Math.random() * 0.4; // Tighter range for consistency

      Matter.Body.applyForce(snowflake, snowflake.position, {
        x: (Math.random() - 0.5) * jumpForce * 0.2, // Less horizontal drift
        y: -jumpForce * randomness,
      });

      // Store initial Y position to track when to start fading
      (snowflake as any).jumpStartY = snowflake.position.y;
      (snowflake as any).targetJumpHeight = this.height * this.MAX_JUMP_HEIGHT;

      // Mark for immediate fade tracking (will check height in update)
      this.fadingSnowflakes.set(snowflake, -1); // -1 = not started fading yet
    }
  }

  private updateFadingSnowflakes(): void {
    if (!this.engine) return;

    // Early exit if no snowflakes are fading
    if (this.fadingSnowflakes.size === 0) return;

    const now = Date.now();

    this.fadingSnowflakes.forEach((fadeStartTime, snowflake) => {
      // Check if we should start fading (halfway to target height)
      if (fadeStartTime === -1) {
        const jumpStartY = (snowflake as any).jumpStartY;
        const targetHeight = (snowflake as any).targetJumpHeight;

        if (jumpStartY && targetHeight) {
          const traveledDistance = jumpStartY - snowflake.position.y;
          const halfwayDistance = targetHeight * this.FADE_START_HEIGHT_RATIO;

          // Start fading when reached halfway point
          if (traveledDistance >= halfwayDistance) {
            this.fadingSnowflakes.set(snowflake, now);
            return;
          }

          // Safety check: If snowflake fell back down or is moving down again, reset it
          if (
            snowflake.position.y >= jumpStartY - 5 || // Back to starting position
            (snowflake.velocity.y > 0.1 && traveledDistance < halfwayDistance * 0.3) // Falling down early
          ) {
            // Remove from fading map and clean up jump tracking
            this.fadingSnowflakes.delete(snowflake);
            delete (snowflake as any).jumpStartY;
            delete (snowflake as any).targetJumpHeight;
            return;
          }
        }
        return; // Don't fade yet
      }

      // Calculate fade progress
      const elapsed = now - fadeStartTime;
      const fadeProgress = Math.min(elapsed / this.FADE_DURATION, 1);

      // Update opacity
      snowflake.render.opacity = 0.85 * (1 - fadeProgress);

      // Remove completely faded snowflakes
      if (fadeProgress >= 1) {
        Matter.Composite.remove(this.engine!.world, snowflake);
        const index = this.snowflakes.indexOf(snowflake);
        if (index > -1) {
          this.snowflakes.splice(index, 1);
        }
        this.fadingSnowflakes.delete(snowflake);
        this.snowflakeBirthTimes.delete(snowflake); // Clean up birth time tracking
        // Clean up jump tracking
        delete (snowflake as any).jumpStartY;
        delete (snowflake as any).targetJumpHeight;
      }
    });
  }

  private createSnowflake(): void {
    if (!this.engine) return;

    // Use cached dimensions
    // Random position across extended area (5% beyond screen on each side)
    const x = -this.horizontalExtension + Math.random() * (this.width + this.horizontalExtension * 2);
    const y = -this.verticalExtension - 20; // Start above the screen with extension

    // Random size for variety (matching original: 2-6px)
    const radius = 3 + Math.random() * 3;

    // Create circular snowflake with soft physics and overlap capability
    const snowflake = Matter.Bodies.circle(x, y, radius, {
      restitution: 0.01, // Almost no bounce (reduced from 0.1 to prevent dancing on tractor)
      friction: 1.2, // High friction to prevent sliding and bouncing
      frictionAir: 0.025, // Lower air friction for normal falling speed (was 0.08)
      density: 0.0005, // Very light and airy
      label: 'snowflake',
      slop: 0.9, // Allow slight overlap/interpenetration for natural stacking
      render: {
        fillStyle: '#ffffff',
        //opacity: 0.85,
      },
    });

    // Very gentle initial velocity
    Matter.Body.setVelocity(snowflake, {
      x: (Math.random() - 0.5) * 0.5, // Minimal horizontal drift
      y: 0.2 + Math.random() * 0.3, // Very slow fall
    });

    this.snowflakes.push(snowflake);
    this.snowflakeBirthTimes.set(snowflake, Date.now()); // Track birth time
    Matter.Composite.add(this.engine.world, snowflake);
  }

  private handleResize(): void {
    if (!this.render || !this.engine) return;

    // Update cached dimensions
    this.updateDimensions();

    // Update render options FIRST
    this.render.options.width = this.width;
    this.render.options.height = this.height;

    // Update render bounds (both min and max)
    this.render.bounds.min.x = 0;
    this.render.bounds.min.y = 0;
    this.render.bounds.max.x = this.width;
    this.render.bounds.max.y = this.height;

    // Update canvas size with proper pixel ratio
    const pixelRatio = window.devicePixelRatio;
    this.render.canvas.width = this.width * pixelRatio;
    this.render.canvas.height = this.height * pixelRatio;
    this.render.canvas.style.width = `${this.width}px`;
    this.render.canvas.style.height = `${this.height}px`;

    // Reset the 2D context to prevent accumulated transforms
    const context = this.render.context;
    if (context) {
      context.setTransform(1, 0, 0, 1, 0, 0);
    }

    // Clear the texture cache to prevent "Cannot read properties of undefined" errors
    // This forces Matter.js to reload textures at the correct scale
    if ((this.render as any).textures) {
      // Clear all cached textures
      (this.render as any).textures = {};
    }

    // Re-create boundaries
    const world = this.engine.world;
    const bodies = Matter.Composite.allBodies(world);

    // Remove old boundaries
    bodies.forEach(body => {
      if (body.label === 'floor' || body.label === 'leftWall' || body.label === 'rightWall') {
        Matter.Composite.remove(world, body);
      }
    });

    // Add new boundaries using cached dimensions
    const wallThickness = 1;
    const extendedWidth = this.width + this.horizontalExtension * 2;
    const wallHeight = this.height + this.verticalExtension; // Only extend top for walls

    const boundaries = [
      // Floor at bottom of visible area (no extension)
      Matter.Bodies.rectangle(this.width / 2, this.floorHeight, extendedWidth, wallThickness, {
        isStatic: true,
        label: 'floor',
        render: {
          fillStyle: this.showDebug() ? '#666' : 'transparent',
        },
      }),
      // Left wall beyond left edge (extends only at top)
      Matter.Bodies.rectangle(-this.horizontalExtension, (this.height - this.verticalExtension) / 2, wallThickness, wallHeight, {
        isStatic: true,
        label: 'leftWall',
        render: {
          fillStyle: this.showDebug() ? '#666' : 'transparent',
        },
      }),
      // Right wall beyond right edge (extends only at top)
      Matter.Bodies.rectangle(
        this.width + this.horizontalExtension,
        (this.height - this.verticalExtension) / 2,
        wallThickness,
        wallHeight,
        {
          isStatic: true,
          label: 'rightWall',
          render: {
            fillStyle: this.showDebug() ? '#666' : 'transparent',
          },
        }
      ),
    ];

    Matter.Composite.add(world, boundaries);

    // Update plow Y position after resize using the same calculation as createPlow()
    if (this.plow) {
      // Recalculate proper Y position based on tractor bounds (same as createPlow)
      const scale = 0.4;
      const originalVertices: Matter.Vector[] = [
        { x: 618.6, y: 360.1 },
        { x: 583.3, y: 316.8 },
        { x: 577, y: 264.1 },
        { x: 615.9, y: 218.4 },
        { x: 638.7, y: 220.5 },
        { x: 639.4, y: 206.6 },
        { x: 338.6, y: 148.4 },
        { x: 340, y: 36.8 },
        { x: 114, y: 33.3 },
        { x: 108.5, y: 151.2 },
        { x: 50.9, y: 187.9 },
        { x: 50.9, y: 360.2 },
      ];
      const scaledVertices = Matter.Vertices.scale(originalVertices, scale, scale, Matter.Vector.create(0, 0));
      const tractorSize = Matter.Bounds.create(scaledVertices);

      const weirdOffsetFix = 25;

      // Use same simple calculation as createPlow()
      const height = tractorSize.max.y - tractorSize.min.y;
      const newPlowY = this.floorHeight - height / 2 + weirdOffsetFix + 2;
      (this.plow as any).fixedY = newPlowY;
      Matter.Body.setPosition(this.plow, {
        x: this.plow.position.x,
        y: newPlowY,
      });
    }
  }

  toggleDebug(): void {
    this.showDebug.update(v => !v);
    if (this.render) {
      this.render.options.wireframes = this.showDebug();

      // Update boundary visibility
      if (this.engine) {
        const bodies = Matter.Composite.allBodies(this.engine.world);
        bodies.forEach(body => {
          if (body.label === 'floor' || body.label === 'leftWall' || body.label === 'rightWall') {
            body.render.fillStyle = this.showDebug() ? '#666' : 'transparent';
          }
        });
      }
    }
  }

  toggleStats(): void {
    this.showStats.update(v => !v);
    if (this.render) {
      this.render.options.showStats = this.showStats();
      this.render.options.showPerformance = this.showStats();
    }
  }

  ngOnDestroy(): void {
    // Clean up
    if (this.snowInterval) {
      clearInterval(this.snowInterval);
    }

    if (this.windForceInterval) {
      clearInterval(this.windForceInterval);
    }

    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    if (this.handleResizeBound) {
      window.removeEventListener('resize', this.handleResizeBound);
    }

    if (this.handleScrollBound) {
      window.removeEventListener('scroll', this.handleScrollBound);
    }

    if (this.handleKeyDownBound) {
      window.removeEventListener('keydown', this.handleKeyDownBound);
    }

    if (this.handleKeyUpBound) {
      window.removeEventListener('keyup', this.handleKeyUpBound);
    }

    if (this.render) {
      Matter.Render.stop(this.render);
      this.render.canvas.remove();
    }

    if (this.runner && this.engine) {
      Matter.Runner.stop(this.runner);
    }

    if (this.engine) {
      Matter.Engine.clear(this.engine);
    }

    // Clear maps
    this.fadingSnowflakes.clear();
    this.snowflakeBirthTimes.clear();
  }
}
